'use client';

import { LoadingDark, Typography } from '@musinsa/mds';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { PageViewTracker } from '../common/components';
import { curatorQueries } from '../query-factory';
import { useShopEditContext } from './ShopEditContext';
import { ShopGoodsCurationEdit } from './ShopGoodsCurationEdit';
import { ShopSnapCurationEdit } from './ShopSnapCurationEdit';

interface Props {
  className?: string;
  curationId: string;
}

export function ShopCurationEdit({ className, curationId }: Props) {
  const { isWebview } = useDeviceInfo();
  const { setCurationKind } = useShopEditContext();

  const curationQuery = useQuery({
    ...curatorQueries.curations.detail({ curationId }),
    enabled: Boolean(curationId),
  });

  useEffect(() => {
    const data = curationQuery.data;
    if (!data) {
      return;
    }
    setCurationKind(data.type === 'SNAP2' ? 'snap' : 'goods');
  }, [curationQuery.data, setCurationKind]);

  if (curationQuery.isPending) {
    return isWebview ? null : (
      <div className="flex flex-1 items-center justify-center grow">
        <LoadingDark size="size24x24" />
      </div>
    );
  }

  if (curationQuery.isError || !curationQuery.data) {
    return (
      <div className={twMerge(className, 'w-full flex items-center justify-center py-8 grow')}>
        <Typography>큐레이션을 불러오는 중 오류가 발생했습니다.</Typography>
      </div>
    );
  }

  const curation = curationQuery.data;

  if (curation.type === 'SNAP2') {
    return (
      <>
        <PageViewTracker pageName="curator_shop_edit_snap" musinsaPageName="" />
        <ShopSnapCurationEdit className={className} curation={curation} />
      </>
    );
  }

  if (curation.type === 'GOODS' || curation.type == null) {
    return (
      <>
        <PageViewTracker pageName="curator_select_product" musinsaPageName="" />
        <ShopGoodsCurationEdit className={className} curation={curation} />
      </>
    );
  }

  return (
    <div className={twMerge(className, 'w-full flex items-center justify-center py-8 grow')}>
      <Typography>지원하지 않는 큐레이션 유형입니다.</Typography>
    </div>
  );
}
